import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { MODIFY, PRODUCT_ACTIVITY_DATA, useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon } from "@foodpilot/foods";
import { hasActivityDataSpace } from "src/services/ProductActivityDataSpace";
import { Product } from "src/api/client-api/products/useGetOneProduct";

export type ActivitiesHeaderProps = {
    product: Product;
    toggleEditMode: boolean;
    onActionClick: () => void;
    areActivitiesSaving?: boolean;
};

export const ActivitiesHeader = (props: ActivitiesHeaderProps) => {
    const { t } = useTranslation();
    const { canUser, selectedCompany } = useFoodpilotContext();
    const { product, toggleEditMode, onActionClick, areActivitiesSaving = false } = props;

    const navigate = useNavigate();
    if (selectedCompany === null) return;

    const isProductOwner = selectedCompany?.id === product.ownedBy?.id;
    const canModifyActivityData = canUser(MODIFY, PRODUCT_ACTIVITY_DATA);

    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100px" }}>
            <Box sx={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                <Typography variant="h3">
                    {t("product.activity_data.associated_structures", "Associated structures")}
                </Typography>
                {isProductOwner && (
                    <>
                        <Typography variant="huge">-</Typography>
                        <Typography variant="huge">{product.shares?.length ?? 0}</Typography>
                    </>
                )}
            </Box>
            {(isProductOwner || hasActivityDataSpace(product, selectedCompany.id)) && canModifyActivityData && (
                <Box sx={{ display: "flex", gap: "1rem" }}>
                    {isProductOwner && product.canModify && (
                        <Button
                            variant="ternary"
                            startIcon={<FoodsIcon size={4} icon={"checkList"} />}
                            onClick={() => {
                                const returnUrl = URLHandler.product.activity(selectedCompany.id, product.id);
                                const url = URLHandler.product.edit(selectedCompany.id, product.id);

                                navigate(`${url}?returnUrl=${returnUrl}`);
                            }}
                            disabled={toggleEditMode}
                        >
                            {t("product.activity_data.add_or_remove_structures", "Add or remove associated structures")}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        startIcon={
                            areActivitiesSaving ? <CircularProgress size={20} sx={{ marginRight: 0.5 }} />
                            : toggleEditMode ?
                                <FoodsIcon size={3} icon={"check"} />
                            :   <FoodsIcon size={2} icon={"edit"} />
                        }
                        onClick={() => onActionClick()}
                        disabled={areActivitiesSaving}
                    >
                        {toggleEditMode ?
                            t("product.activity_data.confirm_modifications", "Confirm modifications")
                        :   t("product.activity_data.modify_data", "Modify data")}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
